/**
 * SeriesDeleteModal - Delete scope selection for series (#1208)
 *
 * Options:
 * - Cancel next occurrence only
 * - Cancel all future events in series
 */

import { useState, useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from './Button';
import { countLabel } from '../lib/formatters';

interface SeriesDeleteModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (scope: 'next' | 'all_future') => void;
  isDeleting: boolean;
  seriesTitle: string;
  upcomingCount: number;
}

export function SeriesDeleteModal({
  isOpen,
  onCancel,
  onConfirm,
  isDeleting,
  seriesTitle,
  upcomingCount,
}: SeriesDeleteModalProps) {
  const [selectedScope, setSelectedScope] = useState<'next' | 'all_future'>('next');
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const dialogRef = useRef<HTMLDivElement>(null);

  // React docs "adjust state on prop change" — reset selection on open transition.
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setSelectedScope('next');
    }
  }

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, isDeleting]);

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector('button');
      firstButton?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selectedScope);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isDeleting) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="series-delete-title"
        aria-describedby="series-delete-description"
        className="bg-oro-surface border border-oro-border rounded-lg max-w-sm w-full p-6 shadow-card animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-oro-error/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-oro-error" />
          </div>
          <div>
            <h3
              id="series-delete-title"
              className="text-lg font-semibold text-text-primary"
            >
              Cancel Series
            </h3>
            <p
              id="series-delete-description"
              className="text-text-muted text-sm"
            >
              Cancel "{seriesTitle}"?
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3 mb-6">
          {/* Next event only */}
          <button
            type="button"
            onClick={() => setSelectedScope('next')}
            disabled={isDeleting}
            className={`w-full p-4 rounded-lg border text-left transition-colors ${
              selectedScope === 'next'
                ? 'border-oro-error bg-oro-error/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                selectedScope === 'next'
                  ? 'border-oro-error'
                  : 'border-text-muted'
              }`}>
                {selectedScope === 'next' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-oro-error" />
                )}
              </div>
              <div>
                <p className="font-medium text-text-primary">Next event only</p>
                <p className="text-sm text-text-secondary mt-1">
                  Cancel only the next occurrence. Other events remain scheduled.
                </p>
              </div>
            </div>
          </button>

          {/* All future events */}
          <button
            type="button"
            onClick={() => setSelectedScope('all_future')}
            disabled={isDeleting}
            className={`w-full p-4 rounded-lg border text-left transition-colors ${
              selectedScope === 'all_future'
                ? 'border-oro-error bg-oro-error/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                selectedScope === 'all_future'
                  ? 'border-oro-error'
                  : 'border-text-muted'
              }`}>
                {selectedScope === 'all_future' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-oro-error" />
                )}
              </div>
              <div>
                <p className="font-medium text-text-primary">
                  {upcomingCount === 1 ? 'This upcoming event' : `All ${countLabel(upcomingCount, 'upcoming event')}`}
                </p>
                <p className="text-sm text-text-secondary mt-1">
                  Cancel all upcoming events in this series. Past events remain.
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Warning */}
        <div className="mb-6 p-3 rounded-lg bg-oro-error/10 border border-oro-error/20">
          <p className="text-sm text-oro-error">
            This action cannot be undone. All event data and check-ins will be permanently removed.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Keep Events
          </Button>
          <Button
            variant="primary"
            className="flex-1 bg-oro-error hover:bg-oro-error/80 border-oro-error"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Canceling...' : 'Cancel Events'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SeriesDeleteModal;
